import { useState, useEffect, useCallback } from "react";
import { AlertProps, SearchSelectOptionProps } from "@alm/flow-ds/components";
import axios from "axios";

import { CommonPropertyProps } from "schemas/elements";
import SearchSelectContainer from "components/FormDesigner/elements/SearchSelect/SearchSelectContainer";
import api from "lib/api";
import { status } from "../../../../constants";

type Props = Omit<CommonPropertyProps, "value"> & {
	value: SearchSelectOptionProps;
	addAlert: (alert: string, status: AlertProps["status"]) => void;
};

const DEFINITION_SELECTS = ["createLink", "updateLink"];

export default ({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	addAlert,
}: Props) => {
	const [definitions, setDefinitions] = useState<SearchSelectOptionProps[]>(
		[]
	);
	const [loading, setLoading] = useState<boolean>(true);

	const isDefinitionsSelect = DEFINITION_SELECTS.includes(field);

	const getDefinitionList = useCallback(async () => {
		// Gets list of ID Definitions for NestedView or MultiReferneceView
		try {
			const res = await api.definitions.getDefinitions();
			setDefinitions(res as SearchSelectOptionProps[]);
		} catch (error) {
			addAlert("errorLoadingFormList", status.ERROR);
			if (error && axios.isAxiosError(error))
				console.error(`Error: ${error.message}`);
		}
		setLoading(false);
	}, [addAlert]);

	useEffect(() => {
		if (!isDefinitionsSelect) getDefinitionList();
	}, [getDefinitionList, isDefinitionsSelect]);

	const { loadPageOptions } = useDefinitionsSearchSelect();

	const props = {
		updateStore,
		field,
		required,
		value,
		desc,
		validation,
		elementIndex,
		loading:
			field === "updateLink" || field === "createLink"
				? undefined
				: loading,
		options: definitions,
		disabled: definitions.length || isDefinitionsSelect ? false : true,
		isDefinitionsSelect,
		loadPageOptions: isDefinitionsSelect ? loadPageOptions : undefined,
	};

	return <SearchSelectContainer {...props} />;
};

const useDefinitionsSearchSelect = () => {
	const DEFAULT_PAGE_SIZE = 10;
	const [page, setPage] = useState<number>(0);
	const [inputText, setInputText] = useState("");

	const loadPageOptions = async (
		currentInput: string,
		currentOptions = []
	) => {
		if (currentInput !== inputText) {
			setPage(0);
			setInputText(currentInput);
		}

		const [definitions, hasMore] =
			(await api.definitions.getPaginatedDefinitions({
				offset: currentInput !== inputText ? 0 : page,
				limit: DEFAULT_PAGE_SIZE,
				name: currentInput || "",
			})) || [];

		setPage(page => page + 1);

		return {
			options: definitions,
			hasMore,
		};
	};

	return { loadPageOptions };
};
